import { Col, Form, Select } from "antd";
import { createContext, useContext, useEffect, useRef, useState } from "react";
import {
  GET_BUYER_ENDPOINT,
  GET_BUYING_OFFICE_ENDPOINT,
  SALES_CONTRACT_LIST,
  STYLE_MERCHANDISING_LIST,
} from "../../../apiServices/API_ENDPOINTS";
import { getData } from "../../../apiServices/common";
import { isArrayAndHasValue } from "../../../utils/functions";

// Round 2 (B, R3): the page's non-destructive previewFunction is only reachable from
// the shared SearchForm's own "Preview" button (isPreview mode); SearchForm/CustomFilter
// are out of this task's allow-list so there's no prop path to call it from here on
// mount. The page provides it via this context instead.
export const PostCostingReportPreviewContext = createContext(() => {});

const PostCostingReportFilter = ({ initialTableData }) => {
  // States
  const [salesContractList, setSalesContractList] = useState([]);
  const [styleList, setStyleList] = useState([]);
  const [buyingOfficeList, setBuyingOfficeList] = useState([]);
  const [buyerList, setBuyerList] = useState([]);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  // Antd
  const { Option } = Select;
  const form = Form.useFormInstance();
  const previewFunction = useContext(PostCostingReportPreviewContext);

  const mountedRef = useRef(true);
  const reqIdRef = useRef(0);
  const didSeedDefaultRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    getSalesContractList();
    getBuyerList();
    getBuyingOfficeList();
    getStyleList();
  }, []);

  const getSalesContractList = async (filterValues) => {
    const myReq = ++reqIdRef.current;
    const bodyData = {
      per_page: filterValues?.per_page || 20,
      reference_no: filterValues?.reference_no || "",
      // Round 2 (B, R3): SalesContractController@index now honours this (backward
      // compatible; default unchanged) so the "most recently updated" contract is
      // the first row without raising per_page.
      // ux2(final-2): renamed sort/order -> order_by/order_dir (whole-branch review I-2 —
      // sort/order collided with the Kyslik sortable() trait's own request('sort')/request('direction')).
      order_by: "updated_at",
      order_dir: "desc",
    };
    const response = await getData(SALES_CONTRACT_LIST, false, bodyData);

    if (!mountedRef.current || myReq !== reqIdRef.current) return;

    if (response && response?.data?.code === 200) {
      setSalesContractList(response?.data?.data?.data || []);
    }
    setHasLoadedOnce(true);
  };
  const getStyleList = async (filterValues) => {
    const bodyData = {
      per_page: filterValues?.per_page || 20,
      style_no: filterValues?.style_no || "",
    };
    const response = await getData(STYLE_MERCHANDISING_LIST, false, bodyData);

    if (response && response?.data?.code === 200) {
      setStyleList(response?.data?.data?.data || []);
    }
  };
  const getBuyerList = async (filterValues) => {
    const bodyData = {
      per_page: filterValues?.per_page || 20,
      name: filterValues?.name || "",
    };
    const response = await getData(GET_BUYER_ENDPOINT, false, bodyData);

    if (response && response?.data?.code === 200) {
      setBuyerList(response?.data?.data?.data || []);
    }
  };
  const getBuyingOfficeList = async (filterValues) => {
    const bodyData = {
      per_page: filterValues?.per_page || 20,
      name: filterValues?.name || "",
    };
    const response = await getData(GET_BUYING_OFFICE_ENDPOINT, false, bodyData);

    if (response && response?.data?.code === 200) {
      setBuyingOfficeList(response?.data?.data?.data || []);
    }
  };

  // Round 2 (B, R3): pre-select the most recently updated sales contract (already
  // sorted by the backend) and load its preview once, the same way clicking Preview
  // would. Seeds only from the initial, unfiltered load.
  useEffect(() => {
    if (didSeedDefaultRef.current) return;
    if (!hasLoadedOnce) return;
    didSeedDefaultRef.current = true;
    if (!salesContractList.length) return;

    const defaultId = salesContractList[0]?.id;
    if (!defaultId) return;

    form?.setFieldsValue({ sales_contract_id: defaultId });
    previewFunction({ sales_contract_id: defaultId });
  }, [hasLoadedOnce, salesContractList, form, previewFunction]);

  return (
    <>
      <Col span={4}>
        <Form.Item label="Sales Contract No" name="sales_contract_id">
          <Select
            showSearch
            placeholder="Select a Sales Contract No"
            optionFilterProp="children"
            size="small"
            onSearch={(value) => getSalesContractList({ reference_no: value })}
            notFoundContent={
              hasLoadedOnce
                ? "No sales contracts found to pre-select."
                : undefined
            }
          >
            {isArrayAndHasValue(salesContractList) &&
              salesContractList.map((sup) => (
                <Option value={sup.id} key={sup.id}>
                  {sup.reference_no}
                </Option>
              ))}
          </Select>
        </Form.Item>
      </Col>
    </>
  );
};

export default PostCostingReportFilter;
